import React from "react"
import { graphql } from "gatsby"
import {
  useTranslation,
  Trans,
  Link,
} from "@zestia/gatsby-plugin-react-i18next"
import { GatsbyImage, getImage } from "gatsby-plugin-image"

import findImage from "@/utils/find-image"
import useHeadTranslation from "@/hooks/use-head-translation"

import MainLayout from "@/layouts/MainLayout"
import SharedHead from "@/components/SharedHead"

import SecondaryNav from "@/components/SecondaryNav"
import AffiliateHeader from "@/components/AffiliateHeader"
import QuoteLarge from "@/components/Quote"
import StepByStepList from "@/components/StepByStepList"
import SplitPane from "@/components/SplitPane"
import PartnerTable from "@/components/PartnerTable"
import FullWidthCTABanner from "@/components/FullWidthCTABanner"
import BenefitsList from "@/components/BenefitsList"

import AffiliateIcon from "@/icons/affiliate.svg"

export const Head = props => {
  const { t } = useHeadTranslation(props)

  return (
    <SharedHead
      {...props}
      title={t("affiliate.meta-title")}
      description={t("affiliate.meta-description")}
    />
  )
}

const AffiliateHome = ({ data }) => {
  const { t } = useTranslation()

  const tableData = [
    {
      title: t("affiliate.table.data.commission.title"),
      bronze: t("affiliate.table.data.commission.bronze"),
      silver: t("affiliate.table.data.commission.silver"),
      gold: t("affiliate.table.data.commission.gold"),
    },
    {
      title: t("affiliate.table.data.criteria.title"),
      bronze: t("affiliate.table.data.criteria.bronze"),
      silver: t("affiliate.table.data.criteria.silver"),
      gold: t("affiliate.table.data.criteria.gold"),
    },
    {
      title: t("affiliate.table.data.resources.title"),
      bronze: true,
      silver: true,
      gold: true,
    },
    {
      title: t("affiliate.table.data.assets.title"),
      bronze: true,
      silver: true,
      gold: true,
    },
    {
      title: t("affiliate.table.data.landing-pages.title"),
      bronze: false,
      silver: true,
      gold: true,
    },
    {
      title: t("affiliate.table.data.check-ins.title"),
      bronze: false,
      silver: true,
      gold: true,
    },
    {
      title: t("affiliate.table.data.reports.title"),
      bronze: false,
      silver: true,
      gold: true,
    },
  ]

  return (
    <MainLayout>
      <SecondaryNav
        navTitle={t("partner-program.navigation.title")}
        navTitleLink="/partner-program/"
        items={[
          {
            name: t("partner-program.navigation.items.affiliate"),
            link: "/partner-program/affiliate",
          },
          {
            name: t("partner-program.navigation.items.solution"),
            link: "/partner-program/solution",
          },
          {
            name: t("partner-program.navigation.items.technology"),
            link: "/partner-program/technology",
          },
          {
            name: t("partner-program.navigation.items.smb"),
            link: "/partner-program/smb",
          },
          {
            name: t("partner-program.navigation.items.terms"),
            link: "/partner-program/affiliate/terms",
          },
        ]}
      />

      <AffiliateHeader
        title={t("affiliate.header.title")}
        primaryButtonText={t("affiliate.header.button")}
        secondaryButtonText={t("affiliate.header.link")}
        images={[
          {
            src: findImage(data.allFile, "affiliate-1"),
            alt: t("affiliate.header.img-description"),
          },
          {
            src: findImage(data.allFile, "affiliate-2"),
            alt: t("affiliate.header.img-description"),
          },
          {
            src: findImage(data.allFile, "affiliate-3"),
            alt: t("affiliate.header.img-description"),
          },
        ]}
      />

      <section className="container-fluid row mt-20 md:mt-30">
        <h2 className="col-md-5">{t("affiliate.earn-revenue.title")}</h2>

        <div className="mt-8 md:mt-0 col-md-6 col-md-offset-1">
          <p className="p--lg">{t("affiliate.earn-revenue.content.1")}</p>
          <p className="mb-0 mt-6 p--lg">
            {t("affiliate.earn-revenue.content.2")}
          </p>
          <p className="mb-0 mt-6 p--lg">
            {t("affiliate.earn-revenue.content.3")}
          </p>
        </div>
      </section>

      <StepByStepList
        className="mt-20 md:mt-30"
        steps={{
          stepOne: {
            heading: t("affiliate.steps.stepOne.heading"),
            subHeading: t("affiliate.steps.stepOne.subHeading"),
          },
          stepTwo: {
            heading: t("affiliate.steps.stepTwo.heading"),
            subHeading: t("affiliate.steps.stepTwo.subHeading"),
          },
          stepThree: {
            heading: t("affiliate.steps.stepThree.heading"),
            subHeading: t("affiliate.steps.stepThree.subHeading"),
          },
          stepFour: {
            heading: t("affiliate.steps.stepFour.heading"),
            subHeading: t("affiliate.steps.stepFour.subHeading"),
          },
        }}
      />

      <QuoteLarge
        quote={t("affiliate.quote.body")}
        cite={{
          name: t("affiliate.quote.cite.name"),
          role_company: t("affiliate.quote.cite.role_company"),
        }}
        footerLink={{
          text: t("affiliate.quote.footer.text"),
          url: t("affiliate.quote.footer.url"),
        }}
        inline="true"
        className="mt-20 md:mt-30"
      />

      <SplitPane
        className="mt-20 md:mt-30"
        assetAlignment="left"
        image={{
          src: findImage(data.allFile, "consultant-client"),
          alt: t("affiliate.why-become-a-partner.img-description"),
        }}
        imageClass="shadow-xl"
        title={t("affiliate.why-become-a-partner.title")}
        titleClass={"h2"}
      >
        <BenefitsList
          benefits={[
            {
              title: t("affiliate.why-become-a-partner.content.one.title"),
              description: t(
                "affiliate.why-become-a-partner.content.one.description",
              ),
            },
            {
              title: t("affiliate.why-become-a-partner.content.two.title"),
              description: t(
                "affiliate.why-become-a-partner.content.two.description",
              ),
            },
            {
              title: t("affiliate.why-become-a-partner.content.three.title"),
              description: t(
                "affiliate.why-become-a-partner.content.three.description",
              ),
            },
            {
              title: t("affiliate.why-become-a-partner.content.four.title"),
              description: t(
                "affiliate.why-become-a-partner.content.four.description",
              ),
            },
          ]}
        />
      </SplitPane>

      <PartnerTable
        className="mt-20 md:mt-30 container-fluid"
        title={t("affiliate.table.title")}
        icon={AffiliateIcon}
        data={tableData}
      />

      <div className="container-fluid row mt-20 md:mt-30">
        <div className="col-md-5">
          <h2 className="mb-0 h3">{t("common.faq")}</h2>
        </div>

        {/* FAQs */}
        <div className="mt-8 md:mt-0 col-md-6 col-md-offset-1">
          <h3 className="mb-0 h5">{t("affiliate.faq.itemOne.title")}</h3>
          <p>{t("affiliate.faq.itemOne.body")}</p>

          <h3 className="mb-0 h5">{t("affiliate.faq.itemTwo.title")}</h3>
          <p>{t("affiliate.faq.itemTwo.body")}</p>

          <h3 className="mb-0 h5">{t("affiliate.faq.itemThree.title")}</h3>
          <p>{t("affiliate.faq.itemThree.body")}</p>

          <h3 className="mb-0 h5">{t("affiliate.faq.itemFour.title")}</h3>
          <p>
            <Trans
              i18nKey="affiliate.faq.itemFour.body"
              components={{
                Link: (
                  <Link
                    to="/partner-program/affiliate/terms/"
                    className="text-link text-link--bare"
                    language="en"
                  />
                ),
              }}
            />
          </p>

          <h3 className="mb-0 h5">{t("affiliate.faq.itemFive.title")}</h3>
          <p>
            <Trans
              i18nKey="affiliate.faq.itemFive.body"
              components={{
                Link: (
                  <Link
                    to="/partner-program/affiliate/terms/#payments"
                    className="text-link text-link--bare"
                    language="en"
                  />
                ),
              }}
            />
          </p>

          <h3 className="mb-0 h5">{t("affiliate.faq.itemSix.title")}</h3>
          <p>
            <Trans
              i18nKey="affiliate.faq.itemSix.body"
              components={{
                Link: (
                  <Link
                    to="/partner-program/affiliate/terms/#referral-fees"
                    className="text-link text-link--bare"
                    language="en"
                  />
                ),
              }}
            />
          </p>
        </div>
      </div>

      <FullWidthCTABanner
        text={t("affiliate.cta.title")}
        buttonLink="https://dash.partnerstack.com/application?company=capsule&group=affiliatepartner"
        buttonText={t("affiliate.cta.button")}
        className="mt-20 md:mt-30"
      />
    </MainLayout>
  )
}

export default AffiliateHome

export const query = graphql`
  query {
    ...Locales
    allFile(
      filter: {
        sourceInstanceName: { eq: "images" }
        relativeDirectory: { eq: "affiliate" }
      }
    ) {
      edges {
        node {
          childImageSharp {
            gatsbyImageData
          }
        }
      }
    }
  }
`
